/**
 * genericBlockObjectName — the web half of Unity's `SceneBlockManager.IsGenericBlockObjectName`
 * (`GenericObjectNameQualifier`), used by previsLoader's placeholder-props flag (20260902).
 *
 * A block whose name is just the app's unnamed-object naming convention — "Object", "Object 13",
 * legacy "Object #13", or the same in any language the app ships, plus the pre-localisation record
 * default "Block" — is NOT a placeholder label, however 'Cube'-shaped it is: it is a plain box the
 * user dropped and never named. Unity keeps it a grey cube; the Enhance prompt must not be told to
 * replace it with a "real named prop" either.
 *
 * The terms are the CanvasEditor/UnnamedObject row of Assets/~Localization/Localization.csv, every
 * language, because the name was baked into the project file under whatever language was active
 * when the block was made. Unity reads them from its localisation database at run time; the web
 * has no access to that database, so they are copied here — when that row changes, this list must.
 */

/** English, Italian, Swedish/German (Objekt), Spanish/Portuguese (Objeto), French, Japanese,
 *  Chinese (Simplified and Traditional share 物件), Korean — plus the legacy record default. */
export const GENERIC_BLOCK_OBJECT_TERMS: readonly string[] = [
  'Object', 'Oggetto', 'Objekt', 'Objeto', 'Objet', 'オブジェクト', '物件', '객체',
  'Block',
];

/**
 * True when `blockName` is a generic unnamed-object name: one of the terms, optionally followed by
 * "#" and/or digits and nothing else ("Object", "Object 13", "Object #13"). Anything after the term
 * that is not that suffix is a name a user chose deliberately. Case-insensitive on the term, as
 * Unity's is; newlines are folded to spaces first, matching its search pre-fill normalisation.
 */
export function isGenericBlockObjectName(blockName: string | null | undefined): boolean {
  if (!blockName) return false;
  const name = blockName.replace(/[\r\n]/g, ' ').trim();
  if (name.length === 0) return false;
  const lower = name.toLowerCase();
  for (const term of GENERIC_BLOCK_OBJECT_TERMS) {
    const t = term.toLowerCase();
    if (!lower.startsWith(t)) continue;
    let remainder = name.slice(term.length).trim();
    if (remainder.startsWith('#')) remainder = remainder.slice(1).trim();
    if (/^\d*$/.test(remainder)) return true;
  }
  return false;
}
